import { Box, Stack, SvgIconProps, SxProps, useTheme } from '@mui/material';
import i18next, { getFixedT } from 'i18next';
import React from 'react';

import type { Theme } from '../../themes/themes';
import { FinalContainer } from '../FinalGrid/FinalGrid';
import { Link } from '../Link/Link';
import { Typography } from '../Typography/Typography';
import { getStyles } from './HeaderBar.styles';

const t = getFixedT.bind(i18next)(null, 'core', 'header');

export interface HeaderBarProps {
  id?: string;
  mobileIcon?: React.ReactElement<SvgIconProps>;
  level1Navigation?: React.ReactElement | null;
  level2Navigation?: React.ReactElement | null;
  level3Navigation?: React.ReactElement | null;
  accessibilityLinks?: React.ReactElement | null;
  searchButton?: React.ReactElement;
  button?: React.ReactElement;
  menuButton?: React.ReactElement;
  homeLinkLabel?: string;
  hideLevel1navigation?: boolean;
  optionsToggle?: React.ReactElement;
  variant?: 'default' | 'flyout';
  baseUrl?: string;
  baseUrlPath?: string;
  customLogoUrl?: string;
  customFlyoutLogoUrl?: string;
  customHeaderIconStyle?: SxProps;
  stickyHeaderBar?: boolean;
  skipToMainContentTarget?: string;
  skipToMainContentOnClick?: React.MouseEventHandler<HTMLAnchorElement> | null;
  skipToMainContentOnKeyDown?: React.KeyboardEventHandler<HTMLAnchorElement> | null;
  skipToMainContentLinkText?: string;
}

/**  */
export const HeaderBar: React.FC<HeaderBarProps> = ({
  mobileIcon,
  level1Navigation,
  level2Navigation,
  level3Navigation,
  accessibilityLinks,
  searchButton,
  button,
  menuButton,
  homeLinkLabel,
  id,
  hideLevel1navigation = false,
  optionsToggle,
  variant = 'default',
  baseUrl,
  baseUrlPath = '/',
  customLogoUrl = '',
  customFlyoutLogoUrl = '',
  customHeaderIconStyle = {},
  stickyHeaderBar = false,
  skipToMainContentTarget,
  skipToMainContentOnClick,
  skipToMainContentOnKeyDown,
  skipToMainContentLinkText,
}: HeaderBarProps) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme, customLogoUrl, customFlyoutLogoUrl);
  const levelOneStyle = Object.assign({}, styles.levelOne, hideLevel1navigation && styles.hidden);
  const headerIconStyle = Object.assign(
    {},
    styles.headerIcon,
    variant === 'default' ? styles.headerIconDefault : styles.headerIconFlyout,
    customHeaderIconStyle
  );
  /**
   * Setup:
   * - Desktop Logo: put your logo into public/assets/tenant_header_logo.svg
   * - Mobile Icon: set the value of TENANT_ICON in .env, swarm and helm values. The Icon with the corresponding name will be loaded from the icon library.
   *  */

  return (
    <>
      {skipToMainContentTarget && skipToMainContentLinkText && (
        <Link
          aria-label={skipToMainContentLinkText}
          href={skipToMainContentTarget}
          tabIndex={0}
          sx={styles.skipToMainContentLink}
          onClick={skipToMainContentOnClick}
          onKeyDown={skipToMainContentOnKeyDown}
          aria-keyshortcuts="Enter"
          role="link"
        >
          <Typography fontFamily="Sparkasse Medium" color={theme.palette.labeling.secondaryDefault} variant="button">
            {skipToMainContentLinkText}
          </Typography>
        </Link>
      )}

      {(level1Navigation || accessibilityLinks) && (
        <FinalContainer
          component="header"
          disableGutters={true}
          sx={variant === 'default' ? styles.backgroundDefault : undefined}
        >
          <FinalContainer sx={levelOneStyle} display={{ xs: 'none', md: 'flex', lg: 'flex' }} id={`${id}.level1`}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              component="nav"
              aria-label={t('site-navigation.label')}
            >
              {level1Navigation ? level1Navigation : <div />}
              {accessibilityLinks}
            </Box>
          </FinalContainer>
        </FinalContainer>
      )}

      <FinalContainer
        data-sticky-headerbar-scom
        component="header"
        disableGutters={true}
        sx={variant === 'default' ? styles.backgroundDefault : undefined}
        position={stickyHeaderBar ? 'sticky' : 'unset'}
        top={0}
        zIndex={stickyHeaderBar ? theme.zIndex.appBar + 10 : 0}
      >
        <FinalContainer sx={styles.levelTwo} minHeight={{ xs: '72px', lg: '104px' }}>
          <Stack direction="row" alignSelf={{ xs: 'none', lg: 'stretch' }} alignItems="stretch">
            <Box sx={styles.headerIconContainer}>
              <Link
                href={baseUrl ? new URL(baseUrlPath, baseUrl).toString() : '/'}
                aria-label={homeLinkLabel}
                sx={headerIconStyle}
              >
                {mobileIcon}
              </Link>
            </Box>
            {optionsToggle}
            <Box
              display={{ xs: 'none', md: 'flex' }}
              pt={{ md: 1, lg: '20px' }}
              component="nav"
              aria-label={t('main-navigation.label')}
            >
              {level2Navigation}
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Box sx={styles.searchButton}>{searchButton}</Box>
            <Box sx={styles.mobileHidden}>{button}</Box>
            <Box sx={styles.desktopHidden}>{menuButton}</Box>
          </Stack>
        </FinalContainer>
      </FinalContainer>

      {level3Navigation && (
        <FinalContainer component="header" disableGutters={true} sx={styles.backgroundGrey}>
          <FinalContainer sx={styles.levelThree} id={`${id}.level3`}>
            {level3Navigation}
          </FinalContainer>
        </FinalContainer>
      )}
    </>
  );
};
